import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, timer, of } from 'rxjs';
import { filter, take, takeUntil, switchMap, catchError } from 'rxjs/operators';
import {
  ExternalDataService,
  WhatsAppCareQueueItem,
  WhatsAppCareQueueFilterDto,
  WhatsAppCareQueuePaginatedResponse
} from '../../../services/externalDataService.service';
import { AuthService } from '../../../services/auth.service';
import { PermissionService } from '../../../services/permission.service';
import { PhoneFormatterService } from '../../../services/phone-formatter.service';
import { HasPermissionDirective } from '../../../directives/has-permission.directive';
import { PaginationComponent } from '../../shared/pagination/pagination.component';

const POLL_INTERVAL_MS = 30000;

@Component({
  selector: 'app-whatsapp-care-queue',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent, HasPermissionDirective],
  templateUrl: './whatsapp-care-queue.component.html',
  styleUrl: './whatsapp-care-queue.component.css'
})
export class WhatsAppCareQueueComponent implements OnInit, OnDestroy {
  queueResponse: WhatsAppCareQueuePaginatedResponse | null = null;

  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;

  filters: WhatsAppCareQueueFilterDto = {
    pageIndex: 0,
    pageSize: 10,
    includeAttended: false,
    phoneNumber: ''
  };

  loading = false;
  errorMessage = '';
  successMessage = '';
  attendingSessionId: string | null = null;

  Math = Math;

  // Cancels the in-flight polling loop (used whenever filters/page change so we
  // don't end up with two overlapping 30s pollers running at once).
  private pollSubscription$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService,
    private phoneFormatterService: PhoneFormatterService,
    public permissionService: PermissionService
  ) { }

  ngOnInit(): void {
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.startPolling();
      });
  }

  ngOnDestroy(): void {
    this.pollSubscription$.next();
    this.pollSubscription$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private startPolling(): void {
    // Cancel any previous poller before starting a fresh one.
    this.pollSubscription$.next();
    this.pollSubscription$.complete();
    this.pollSubscription$ = new Subject<void>();

    this.filters.pageIndex = this.currentPage - 1;
    this.filters.pageSize = this.pageSize;

    const activeFilter: WhatsAppCareQueueFilterDto = {
      ...this.filters,
      phoneNumber: this.filters.phoneNumber?.trim() || undefined
    };

    this.loading = true;
    this.errorMessage = '';

    timer(0, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          this.externalDataService.getWhatsAppCareQueue(activeFilter).pipe(
            catchError((error) => {
              this.handleLoadError(error);
              return of(null);
            })
          )
        ),
        takeUntil(this.pollSubscription$),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        this.loading = false;
        if (response) {
          this.queueResponse = response;
          this.totalCount = response.totalCount || 0;
          this.calculateTotalPages();
        }
      });
  }

  private handleLoadError(error: any): void {
    console.error('Error loading WhatsApp care queue:', error);
    this.queueResponse = null;
    this.totalCount = 0;
    this.totalPages = 1;

    if (error.status === 401) {
      this.errorMessage = 'Authentication required. Please log in again.';
    } else if (error.status === 403) {
      this.errorMessage = 'Access denied. You do not have permission to view the care queue.';
    } else {
      this.errorMessage = 'Failed to load the care queue. Please try again.';
    }
  }

  calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.startPolling();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.startPolling();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.startPolling();
  }

  clearFilters(): void {
    this.filters = {
      pageIndex: 0,
      pageSize: this.pageSize,
      includeAttended: false,
      phoneNumber: ''
    };
    this.currentPage = 1;
    this.startPolling();
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.phoneNumber?.trim() || this.filters.includeAttended);
  }

  markAttended(item: WhatsAppCareQueueItem): void {
    if (item.isAttended || this.attendingSessionId) {
      return;
    }

    this.attendingSessionId = item.id;
    this.errorMessage = '';
    this.successMessage = '';

    this.externalDataService.markWhatsAppCareQueueItemAttended(item.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.attendingSessionId = null;
          this.successMessage = `Marked ${updated.phoneNumber} as attended.`;
          this.startPolling();
        },
        error: (error) => {
          console.error('Error marking session attended:', error);
          this.attendingSessionId = null;
          this.errorMessage = error.error?.detail || 'Failed to mark session as attended. Please try again.';
        }
      });
  }

  getWhatsAppLink(phoneNumber: string): string {
    const storageFormat = this.phoneFormatterService.getStorageFormat(phoneNumber);
    const digits = (storageFormat || phoneNumber || '').replace(/\D/g, '');
    return `https://wa.me/${digits}`;
  }

  openWhatsApp(phoneNumber: string): void {
    window.open(this.getWhatsAppLink(phoneNumber), '_blank');
  }

  getHandoffReasonLabel(reason: string | null | undefined): string {
    switch (reason) {
      case 'CustomerCareRequested':
        return 'Requested customer care';
      case 'UnexpectedInput':
        return 'Unrecognized input';
      default:
        return reason || '—';
    }
  }

  getLanguageLabel(language: string | null | undefined): string {
    switch (language) {
      case 'en':
        return 'English';
      case 'ml':
        return 'Malayalam';
      default:
        return '—';
    }
  }

  // No relative-time pipe/util exists elsewhere in this codebase, so this is a
  // small self-contained helper rather than a new shared pipe.
  getTimeSince(dateStr: string | null | undefined): string {
    if (!dateStr) {
      return '—';
    }
    const then = new Date(dateStr).getTime();
    if (Number.isNaN(then)) {
      return '—';
    }
    const diffMs = Date.now() - then;
    if (diffMs < 0) {
      return 'just now';
    }
    const diffSeconds = Math.floor(diffMs / 1000);
    if (diffSeconds < 60) {
      return 'just now';
    }
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) {
      return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
    }
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    }
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }
}
